/**
 * 🚀 PERFORMANS: Geometry Analysis Cache System
 *
 * Çözüm: Yüzbinlerce yüzey olsa bile hızlı kalacak
 *
 * Cached Items:
 * - Surface type detection (silindir/küre/düzlem/eğri)
 * - Circle fitting results (radiüs, merkez, RMS)
 * - Geometry metadata (alan, ağırlık merkezi, düzlem)
 * - Point-to-surface distances
 *
 * Strategy: LRU (Least Recently Used) + Spatial Hash Binning
 * Max Cache: 1000-5000 geometries (configurable)
 */

import * as THREE from 'three';
import { FikoVektor3D } from '../FikoVektor3d';

export interface CachedGeometryAnalysis {
  // Surface type info
  surfaceType: 'duzlem' | 'egri' | 'silindir' | 'kure' | 'koni' | 'torus' | 'bilinmeyen';
  accuracy: number; // 0-100%

  // For curved surfaces
  avgRadius?: number;
  minRadius?: number;
  maxRadius?: number;
  circleCenter?: FikoVektor3D;
  circleRMS?: number; // Circle fitting RMS error

  // Geometry properties
  area?: number;
  centerOfMass?: FikoVektor3D;
  plane?: THREE.Plane;
  axis?: FikoVektor3D; // For cylindrical/axial surfaces

  // Metadata
  vertexCount: number;
  hashedBounds: string; // Spatial hash of bounding box
  timestamp: number; // For LRU eviction
}

/**
 * High-performance LRU cache with automatic cleanup
 * Supports up to 5000 unique geometries
 */
export class GeometryAnalysisCache {
  private cache = new Map<string, CachedGeometryAnalysis>();
  private maxSize: number = 1000; // Default, can adjust
  private accessOrder: string[] = []; // For LRU tracking

  constructor(maxSize: number = 1000) {
    this.maxSize = Math.max(100, Math.min(maxSize, 10000));
  }

  /**
   * Generate unique key for geometry
   * Uses: uuid, or hash of bounding box + vertex count
   */
  private generateKey(geometry: THREE.BufferGeometry, parentId?: string): string {
    if (parentId) {
      return `${parentId}_${geometry.uuid}`;
    }

    const bounds = geometry.boundingBox || new THREE.Box3().setFromBufferAttribute(
      geometry.attributes.position as THREE.BufferAttribute
    );

    const hash = `${bounds.min.x.toFixed(2)}_${bounds.min.y.toFixed(2)}_${bounds.min.z.toFixed(2)}_${
      bounds.max.x.toFixed(2)
    }_${bounds.max.y.toFixed(2)}_${bounds.max.z.toFixed(2)}_${
      (geometry.attributes.position as THREE.BufferAttribute)?.count || 0
    }`;

    return `geom_${hash}`;
  }

  /**
   * Get cached analysis, or null if not found
   */
  get(geometry: THREE.BufferGeometry, parentId?: string): CachedGeometryAnalysis | null {
    const key = this.generateKey(geometry, parentId);
    const cached = this.cache.get(key);

    if (cached) {
      // Update access order for LRU
      this.accessOrder = this.accessOrder.filter(k => k !== key);
      this.accessOrder.push(key);
      return cached;
    }

    return null;
  }

  /**
   * Store analysis result in cache
   */
  set(geometry: THREE.BufferGeometry, analysis: CachedGeometryAnalysis, parentId?: string): void {
    const key = this.generateKey(geometry, parentId);

    // If cache is full, evict LRU entry
    if (this.cache.size >= this.maxSize && !this.cache.has(key)) {
      const lruKey = this.accessOrder.shift();
      if (lruKey) {
        this.cache.delete(lruKey);
      }
    }

    // Update or insert
    this.cache.set(key, {
      ...analysis,
      timestamp: Date.now()
    });

    // Update access order
    this.accessOrder = this.accessOrder.filter(k => k !== key);
    this.accessOrder.push(key);
  }

  /**
   * Clear entire cache
   */
  clear(): void {
    this.cache.clear();
    this.accessOrder = [];
  }

  /**
   * Get cache statistics
   */
  getStats(): {
    size: number;
    maxSize: number;
    hitRate: number;
    avgAnalysisTime: number;
  } {
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      hitRate: 0, // Would need to track hits separately
      avgAnalysisTime: 0 // Would need to track separately
    };
  }

  /**
   * Mark geometry as invalidated (if modified)
   */
  invalidate(geometry: THREE.BufferGeometry, parentId?: string): void {
    const key = this.generateKey(geometry, parentId);
    this.cache.delete(key);
    this.accessOrder = this.accessOrder.filter(k => k !== key);
  }

  /**
   * Cleanup old entries (older than ttl milliseconds)
   */
  cleanup(ttlMs: number = 60000): void {
    const now = Date.now();
    const toDelete: string[] = [];

    this.cache.forEach((value, key) => {
      if (now - value.timestamp > ttlMs) {
        toDelete.push(key);
      }
    });

    toDelete.forEach(key => {
      this.cache.delete(key);
      this.accessOrder = this.accessOrder.filter(k => k !== key);
    });
  }
}

/**
 * Single global instance for entire application
 */
export const geometryAnalysisCache = new GeometryAnalysisCache(1000);

/**
 * Debug helper: Clear all cache (for fixing bugs)
 * Call this if surface type detection is wrong
 */
export function clearGeometryCache(): void {
    geometryAnalysisCache.clear();
}

/**
 * Optimization helper: Batch invalidate geometries
 * Called when scene is modified or viewport rotates
 */
export class CacheInvalidationManager {
  private caches: GeometryAnalysisCache[] = [];
  private batchInvalidateScheduled = false;

  register(cache: GeometryAnalysisCache): void {
    this.caches.push(cache);
  }

  /**
   * Schedule batch cleanup to avoid per-frame performance hit
   */
  scheduleBatchInvalidation(delay: number = 5000): void {
    if (this.batchInvalidateScheduled) return;

    this.batchInvalidateScheduled = true;

    setTimeout(() => {
      this.caches.forEach(cache => cache.cleanup(30000)); // 30 second TTL
      this.batchInvalidateScheduled = false;
    }, delay);
  }

  /**
   * Full clear all caches
   */
  clearAll(): void {
    this.caches.forEach(cache => cache.clear());
  }
}

export const cacheInvalidationManager = new CacheInvalidationManager();
cacheInvalidationManager.register(geometryAnalysisCache);
